<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'

// Dropdown fed by /api/v1/me memberships (our DB, not Clerk's org list).
// Selecting an org routes to its Today page; the session store persists the
// choice and the API client sends it as X-Org-ID on every request.
const router = useRouter()
const session = useSessionStore()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement>()

const currentOrg = computed(() => session.activeOrg)

function selectOrg(orgId: string) {
  isOpen.value = false
  if (orgId === session.activeOrgId) return
  session.setActiveOrg(orgId)
  router.push(`/o/${orgId}/today`)
}

function handleClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
  <div v-if="session.memberships.length" ref="dropdownRef" class="relative">
    <button
      class="flex items-center gap-2 px-3 py-1.5 rounded bg-navy-light text-body-sm text-white
        hover:bg-navy-light/80 transition-colors duration-short max-w-[240px]"
      @click="isOpen = !isOpen"
    >
      <svg class="w-4 h-4 flex-shrink-0 text-white/60" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
      </svg>
      <span class="truncate">{{ currentOrg?.org_name ?? 'Select organization' }}</span>
      <svg
        class="w-3 h-3 flex-shrink-0 text-white/60 transition-transform duration-short"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-medium ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-short ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 top-full mt-2 w-[260px] rounded-lg border border-subtle bg-surface-card
          shadow-lg py-1 z-50 max-h-72 overflow-y-auto"
      >
        <button
          v-for="m in session.memberships"
          :key="m.org_id"
          class="w-full flex items-center gap-2 px-3 py-2 text-left text-body-sm transition-colors duration-short
            hover:bg-surface-muted"
          :class="m.org_id === session.activeOrgId ? 'text-accent font-semibold' : 'text-ink-soft'"
          @click="selectOrg(m.org_id)"
        >
          <span class="truncate flex-1">{{ m.org_name }}</span>
          <span class="text-label-caps uppercase text-ink-muted">{{ m.role }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
